import React, { useEffect, useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { db } from "../../firebase";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle-group";
import { DataTable, DataTableColumn } from "../../components/ui/data-table";
import { Spinner } from "../../components/ui/spinner";
import { DateRangePicker } from "../../components/DateRangePicker";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../components/ui/card";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type ViewMode = "chart" | "table";
type Section = "product" | "report" | "customer";
type ProductOrder = "desc" | "asc";
type ItemSortKey = "amount" | "qty";
type CustomerOrder = "desc" | "asc";

interface RowItem {
  saleDate: string;
  productName: string;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
  customerName: string;
  posAt: string;
  receiptNumber: string;
  affiliation?: string;
 }

const toNum = (v: any, fallback = 0) =>
  Number.isFinite(Number(v)) ? Number(v) : fallback;

const COLORS = [
  "#1E3A8A",
  "#1D4ED8",
  "#2563EB",
  "#3B82F6",
  "#60A5FA",
  "#76B4FB",
  "#93C5FD",
  "#B1D5FE",
  "#cbdbf0",
  "#dae2eb",
];

const chartTooltipProps = {
  contentStyle: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    borderColor: "#e5e7eb",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  },
  labelStyle: { color: "#111827", fontWeight: 600 },
  itemStyle: { color: "#111827" },
};

const getWeekdayKo = (d: dayjs.Dayjs | string) => {
  const w = dayjs(d).day();
  return ["일", "월", "화", "수", "목", "금", "토"][w];
};

const Statistics: React.FC = () => {
  const [affiliations, setAffiliations] = useState<string[]>([]);
  const [selectedAff, setSelectedAff] = useState<string>("전체");
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf("month"),
    dayjs(),
  ]);
  const [view, setView] = useState<ViewMode>("chart");

  const [section, setSection] = useState<Section>("product");
  const [productOrder, setProductOrder] = useState<ProductOrder>("desc");
  const [itemSort, setItemSort] = useState<ItemSortKey>("amount");
  const [customerOrder, setCustomerOrder] = useState<CustomerOrder>("desc");

  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<RowItem[]>([]);

  const fileSuffix = useMemo(() => {
    const aff = selectedAff === "전체" ? "ALL" : selectedAff;
    return `${aff}_${range[0].format("YYYYMMDD")}-${range[1].format(
      "YYYYMMDD"
    )}`;
  }, [selectedAff, range]);

  useEffect(() => {
    const loadAffiliations = async () => {
      try {
        const snap = await getDocs(collection(db, "affiliations"));
        let list: string[] = snap.docs.map((d) => d.id).filter(Boolean);

        if (list.length === 0) {
          const usnap = await getDocs(collection(db, "users"));
          const raw = usnap.docs.map((d) => d.data().affiliation);
          list = Array.from(new Set(raw)).filter(Boolean);
        }

        list = list.filter((n) => n && n !== "총무노트");

        list.sort((a, b) => a.localeCompare(b, "ko"));
        setAffiliations(list);
      } catch (e) {
        console.error(e);
        toast.error("고객사 목록을 불러오지 못했습니다.");
      }
    };
    loadAffiliations();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!range?.[0] || !range?.[1]) return;
      if (selectedAff === "전체" && affiliations.length === 0) return;

      setLoading(true);
      try {
        const start = range[0].startOf("day");
        const end = range[1].endOf("day");

        const targets = selectedAff === "전체" ? affiliations : [selectedAff];
        if (targets.length === 0) {
          setRows([]);
          setLoading(false);
          return;
        }

        const months: string[] = [];
        let cur = start.clone().startOf("month");
        const endM = end.clone().startOf("month");
        while (cur.isSameOrBefore(endM, "month")) {
          months.push(cur.format("YYYY-MM"));
          cur = cur.add(1, "month");
        }

        const tasks = targets.flatMap((aff) =>
          months.map((mk) =>
            getDoc(doc(db, `affiliations/${aff}/salesUploads/${mk}`)).then(
              (snap) => ({ snap, aff })
            )
          )
        );

        const results = await Promise.all(tasks);
        const merged: RowItem[] = [];
        for (const { snap, aff } of results) {
          if (!snap.exists()) continue;
          const data = snap.data() as any;
          const arr: any[] = Array.isArray(data.rows) ? data.rows : [];
          for (const r of arr) {
            if (!r.saleDate) continue;
            const dt = dayjs(r.saleDate);
            if (dt.isSameOrAfter(start, "day") && dt.isSameOrBefore(end, "day")) {
              merged.push({
                saleDate: r.saleDate,
                productName: r.productName,
                unitPrice: toNum(r.unitPrice),
                quantity: toNum(r.quantity, 1),
                totalPrice: toNum(r.totalPrice),
                customerName: r.customerName ?? "",
                posAt: r.posAt ?? "",
                receiptNumber: r.receiptNumber ?? "",
                affiliation: aff,
              });
            }
          }
        }
        setRows(merged);
      } catch (e) {
        console.error(e);
        toast.error("통계 데이터를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedAff, affiliations, range]);

  const productAgg = useMemo(() => {
    const m = new Map<string, { name: string; qty: number; amount: number }>();
    rows.forEach((r) => {
      const key = r.productName || "(미상)";
      const prev = m.get(key) || { name: key, qty: 0, amount: 0 };
      prev.qty += toNum(r.quantity, 1);
      prev.amount += toNum(r.totalPrice);
      m.set(key, prev);
    });
    const arr = Array.from(m.values());
    const desc = [...arr].sort((a, b) => b.qty - a.qty);
    const asc = [...arr].sort((a, b) => a.qty - b.qty);
    return {
      fullDesc: desc,
      fullAsc: asc,
      top10Desc: desc.slice(0, 10),
      top10Asc: asc.slice(0, 10),
    };
  }, [rows]);

  const monthlyAgg = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => {
      const k = dayjs(r.saleDate).format("YYYY-MM");
      m.set(k, (m.get(k) || 0) + toNum(r.totalPrice));
    });
    return Array.from(m.entries())
      .map(([month, total]) => ({ month, total }))
      .sort((a, b) => (a.month < b.month ? -1 : 1));
  }, [rows]);

  const customerAgg = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => {
      const n = r.customerName || "(미상)";
      m.set(n, (m.get(n) || 0) + toNum(r.totalPrice));
    });
    const desc = Array.from(m.entries())
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
    const asc = [...desc].reverse();
    return { desc, asc };
  }, [rows]);

  const customerDataShown = useMemo(
    () =>
      (customerOrder === "desc" ? customerAgg.desc : customerAgg.asc).map(
        (x, i) => ({ key: i, rank: i + 1, ...x })
      ),
    [customerAgg, customerOrder]
  );

  const customerChartTop10 = useMemo(
    () =>
      (customerOrder === "desc" ? customerAgg.desc : customerAgg.asc).slice(
        0,
        10
      ),
    [customerAgg, customerOrder]
  );

  const totalAmount = useMemo(
    () => rows.reduce((s, r) => s + toNum(r.totalPrice), 0),
    [rows]
  );
  const totalCount = useMemo(() => rows.length, [rows]);
  const totalQty = useMemo(
    () => rows.reduce((s, r) => s + toNum(r.quantity, 1), 0),
    [rows]
  );

  const itemReportAgg = useMemo(() => {
    const m = new Map<string, { name: string; qty: number; amount: number }>();
    rows.forEach((r) => {
      const k = r.productName || "(미상)";
      const prev = m.get(k) || { name: k, qty: 0, amount: 0 };
      prev.qty += toNum(r.quantity, 1);
      prev.amount += toNum(r.totalPrice);
      m.set(k, prev);
    });
    const base = Array.from(m.values()).map((x) => ({
      name: x.name,
      qty: x.qty,
      amount: x.amount,
      ratio: totalAmount ? (x.amount / totalAmount) * 100 : 0,
    }));
    const byAmount = [...base].sort((a, b) => b.amount - a.amount);
    const byQty = [...base].sort((a, b) => b.qty - a.qty);
    return { byAmount, byQty };
  }, [rows, totalAmount]);

  const itemReportShown = useMemo(
    () =>
      (itemSort === "amount" ? itemReportAgg.byAmount : itemReportAgg.byQty).map(
        (x, i) => ({ key: i, ...x })
      ),
    [itemReportAgg, itemSort]
  );

  const hourlyReport = useMemo(() => {
    const base = Array.from({ length: 24 }, (_, h) => ({
      hour: h.toString().padStart(2, "0"),
      count: 0,
      amount: 0,
    }));
    const map = new Map(base.map((x) => [x.hour, x]));
    rows.forEach((r) => {
      const h = dayjs(r.saleDate).format("HH");
      const obj = map.get(h)!;
      obj.count += 1;
      obj.amount += toNum(r.totalPrice);
    });
    return Array.from(map.values()).map((x, i) => ({
      key: i,
      hour: `${x.hour}시`,
      count: x.count,
      amount: x.amount,
      ratio: totalAmount ? (x.amount / totalAmount) * 100 : 0,
    }));
  }, [rows, totalAmount]);

  const weekdayReport = useMemo(() => {
    const order = ["일", "월", "화", "수", "목", "금", "토"];
    const m = new Map<string, { qty: number; amount: number }>();
    order.forEach((w) => m.set(w, { qty: 0, amount: 0 }));
    rows.forEach((r) => {
      const w = getWeekdayKo(r.saleDate);
      const obj = m.get(w)!;
      obj.qty += toNum(r.quantity, 1);
      obj.amount += toNum(r.totalPrice);
    });
    return order.map((w, i) => {
      const obj = m.get(w)!;
      return {
        key: i,
        weekday: w,
        qty: obj.qty,
        amount: obj.amount,
        ratio: totalAmount ? (obj.amount / totalAmount) * 100 : 0,
      };
    });
  }, [rows, totalAmount]);

  const maxWeekdayAmount = useMemo(
    () => Math.max(0, ...weekdayReport.map((r) => r.amount)),
    [weekdayReport]
  );
  const maxHourlyAmount = useMemo(
    () => Math.max(0, ...hourlyReport.map((r) => r.amount)),
    [hourlyReport]
  );

  const exportXlsx = (
    filename: string,
    rows: any[],
    headers: {
      key: string;
      label: string;
      format?: (v: any, row: any) => any;
    }[]
  ) => {
    const data = rows.map((r) => {
      const obj: Record<string, any> = {};
      headers.forEach(({ key, label, format }) => {
        const v = (r as any)[key];
        obj[label] = format ? format(v, r) : v;
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "데이터");
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const productColumns: DataTableColumn<any>[] = [
    { title: "순위", dataIndex: "rank", width: 70 },
    { title: "상품명", dataIndex: "name" },
    { title: "판매수량", dataIndex: "qty" },
    {
      title: "매출합계",
      dataIndex: "amount",
      render: (v: number) => v.toLocaleString(),
    },
  ];
  const productDataDesc = useMemo(
    () =>
      productAgg.fullDesc.map((x, i) => ({ key: `d-${i}`, rank: i + 1, ...x })),
    [productAgg.fullDesc]
  );
  const productDataAsc = useMemo(
    () =>
      productAgg.fullAsc.map((x, i) => ({ key: `a-${i}`, rank: i + 1, ...x })),
    [productAgg.fullAsc]
  );

  const monthlyColumns: DataTableColumn<any>[] = [
    { title: "월", dataIndex: "month" },
    {
      title: "매출합계",
      dataIndex: "total",
      render: (v: number) => v.toLocaleString(),
    },
  ];

  const customerColumns: DataTableColumn<any>[] = [
    { title: "순위", dataIndex: "rank", width: 70 },
    { title: "이름", dataIndex: "name" },
    {
      title: "매출합계",
      dataIndex: "total",
      render: (v: number) => v.toLocaleString(),
    },
  ];

  const itemReportCols: DataTableColumn<any>[] = [
    { title: "품목명", dataIndex: "name" },
    { title: "수량", dataIndex: "qty" },
    {
      title: "금액",
      dataIndex: "amount",
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: "구성비(%)",
      dataIndex: "ratio",
      render: (v: number) => v.toFixed(1),
    },
  ];
  const hourlyReportCols: DataTableColumn<any>[] = [
    { title: "시간", dataIndex: "hour" },
    { title: "매출건수", dataIndex: "count" },
    {
      title: "금액",
      dataIndex: "amount",
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: "구성비(%)",
      dataIndex: "ratio",
      render: (v: number) => v.toFixed(1),
    },
  ];
  const weekdayReportCols: DataTableColumn<any>[] = [
    { title: "요일", dataIndex: "weekday" },
    { title: "수량", dataIndex: "qty" },
    {
      title: "금액",
      dataIndex: "amount",
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: "구성비(%)",
      dataIndex: "ratio",
      render: (v: number) => v.toFixed(1),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-extrabold">
            통계{" "}
            <span className="text-sm font-normal text-muted-foreground">
              업로드된 데이터를 바탕으로 통계를 확인하세요.
            </span>
          </h1>
          <ToggleGroup
            type="single"
            value={section}
            onValueChange={(v) => v && setSection(v as Section)}
          >
            <ToggleGroupItem value="product">상품별 판매순위</ToggleGroupItem>
            <ToggleGroupItem value="report">종합 매출 리포트</ToggleGroupItem>
            <ToggleGroupItem value="customer">직원별 매출분석</ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedAff} onValueChange={setSelectedAff}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="고객사 선택" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="전체">전체</SelectItem>
              {affiliations.map((aff) => (
                <SelectItem key={aff} value={aff}>
                  {aff}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DateRangePicker
            value={range}
            onChange={(next) =>
              setRange(next ?? [dayjs().startOf("month"), dayjs()])
            }
          />

          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(v) => v && setView(v as ViewMode)}
          >
            <ToggleGroupItem value="chart">차트 On</ToggleGroupItem>
            <ToggleGroupItem value="table">차트 Off</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
          <Spinner size="lg" />
          <span className="text-sm text-muted-foreground">불러오는 중...</span>
        </div>
      ) : (
        <>
          {section === "product" && (
            <Card>
              <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                <div>
                  <CardTitle>
                    상품별 판매순위{" "}
                    {productOrder === "desc"
                      ? "(많이 팔린 순)"
                      : "(적게 팔린 순)"}
                  </CardTitle>
                  <CardDescription>
                    기간: {range[0].format("YYYY-MM-DD")} ~{" "}
                    {range[1].format("YYYY-MM-DD")}
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <ToggleGroup
                    type="single"
                    value={productOrder}
                    onValueChange={(v) => v && setProductOrder(v as ProductOrder)}
                  >
                    <ToggleGroupItem value="desc">많이 팔린 순</ToggleGroupItem>
                    <ToggleGroupItem value="asc">적게 팔린 순</ToggleGroupItem>
                  </ToggleGroup>
                  <Button
                    variant="outline"
                    onClick={() =>
                      exportXlsx(
                        `상품별_판매순위_${fileSuffix}`,
                        productOrder === "desc" ? productDataDesc : productDataAsc,
                        [
                          { key: "rank", label: "순위" },
                          { key: "name", label: "상품명" },
                          { key: "qty", label: "판매수량" },
                          { key: "amount", label: "매출합계" },
                        ]
                      )
                    }
                  >
                    엑셀 다운로드
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                <div className="grid gap-4 lg:grid-cols-2">
                  {view === "chart" && (
                    <div className="h-[360px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={(productOrder === "desc"
                              ? productAgg.top10Desc
                              : productAgg.top10Asc
                            ).map((d) => ({ ...d, value: d.qty }))}
                            dataKey="value"
                            nameKey="name"
                            outerRadius="80%"
                            label={({ name, value }) => `${name} (${value})`}
                          >
                            {(productOrder === "desc"
                              ? productAgg.top10Desc
                              : productAgg.top10Asc
                            ).map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <RTooltip {...chartTooltipProps} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div>
                    <DataTable
                      size="sm"
                      dataSource={
                        productOrder === "desc" ? productDataDesc : productDataAsc
                      }
                      columns={productColumns}
                      pagination={{ pageSize: 10 }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {section === "report" && (
            <div className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                  <div>
                    <CardTitle>품목별 집계</CardTitle>
                    <CardDescription>
                      합계 수량: {totalQty.toLocaleString()} / 합계 금액:{" "}
                      {totalAmount.toLocaleString()}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <ToggleGroup
                      type="single"
                      value={itemSort}
                      onValueChange={(v) => v && setItemSort(v as ItemSortKey)}
                    >
                      <ToggleGroupItem value="amount">금액 기준</ToggleGroupItem>
                      <ToggleGroupItem value="qty">수량 기준</ToggleGroupItem>
                    </ToggleGroup>
                    <Button
                      variant="outline"
                      onClick={() =>
                        exportXlsx(`품목별_집계_${fileSuffix}`, itemReportShown, [
                          { key: "name", label: "품목명" },
                          { key: "qty", label: "수량" },
                          { key: "amount", label: "금액" },
                          {
                            key: "ratio",
                            label: "구성비(%)",
                            format: (v) => Number(v).toFixed(1),
                          },
                        ])
                      }
                    >
                      엑셀 다운로드
                    </Button>
                  </div>
                </CardHeader>

                <CardContent>
                  <DataTable
                    size="sm"
                    dataSource={itemReportShown}
                    columns={itemReportCols}
                    pagination={{ pageSize: 10 }}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                  <div>
                    <CardTitle>시간대별 집계</CardTitle>
                    <CardDescription>
                      총 건수: {totalCount.toLocaleString()} / 총 금액:{" "}
                      {totalAmount.toLocaleString()}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() =>
                      exportXlsx(`시간대별_집계_${fileSuffix}`, hourlyReport, [
                        { key: "hour", label: "시간" },
                        { key: "count", label: "매출건수" },
                        { key: "amount", label: "금액" },
                        {
                          key: "ratio",
                          label: "구성비(%)",
                          format: (v) => Number(v).toFixed(1),
                        },
                      ])
                    }
                  >
                    엑셀 다운로드
                  </Button>
                </CardHeader>

                <CardContent>
                  <DataTable
                    size="sm"
                    dataSource={hourlyReport}
                    columns={hourlyReportCols}
                    pagination={false}
                    onRow={(record) => ({
                      style:
                        record.amount === maxHourlyAmount
                          ? { fontWeight: 700, color: "#d4380d" }
                          : undefined,
                    })}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                  <CardTitle>요일별 집계</CardTitle>
                  <Button
                    variant="outline"
                    onClick={() =>
                      exportXlsx(`요일별_집계_${fileSuffix}`, weekdayReport, [
                        { key: "weekday", label: "요일" },
                        { key: "qty", label: "수량" },
                        { key: "amount", label: "금액" },
                        {
                          key: "ratio",
                          label: "구성비(%)",
                          format: (v) => Number(v).toFixed(1),
                        },
                      ])
                    }
                  >
                    엑셀 다운로드
                  </Button>
                </CardHeader>

                <CardContent>
                  <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
                    <div className="h-[320px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={weekdayReport.map((d) => ({
                              name: d.weekday,
                              value: d.amount,
                            }))}
                            dataKey="value"
                            nameKey="name"
                            outerRadius="80%"
                            label={({ name, value }) =>
                              `${name} (${Number(value).toLocaleString()})`
                            }
                          >
                            {weekdayReport.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <RTooltip
                            {...chartTooltipProps}
                            formatter={(v: any) => Number(v).toLocaleString()}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div>
                      <DataTable
                        size="sm"
                        dataSource={weekdayReport}
                        columns={weekdayReportCols}
                        pagination={false}
                        onRow={(record) => ({
                          style:
                            record.amount === maxWeekdayAmount
                              ? { fontWeight: 700, color: "#d4380d" }
                              : undefined,
                        })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {section === "customer" && (
            <Card>
              <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                <CardTitle>직원별 매출분석 (Top 10)</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <ToggleGroup
                    type="single"
                    value={customerOrder}
                    onValueChange={(v) => v && setCustomerOrder(v as CustomerOrder)}
                  >
                    <ToggleGroupItem value="desc">높은 순</ToggleGroupItem>
                    <ToggleGroupItem value="asc">낮은 순</ToggleGroupItem>
                  </ToggleGroup>
                  <Button
                    variant="outline"
                    onClick={() =>
                      exportXlsx(
                        `직원별_매출분석_${fileSuffix}`,
                        customerDataShown,
                        [
                          { key: "rank", label: "순위" },
                          { key: "name", label: "이름" },
                          { key: "total", label: "매출합계" },
                        ]
                      )
                    }
                  >
                    엑셀 다운로드
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                <div className="grid gap-4 lg:grid-cols-2">
                  {view === "chart" && (
                    <div className="h-[360px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={customerChartTop10}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis />
                          <RTooltip
                            {...chartTooltipProps}
                            formatter={(v: any) => v.toLocaleString()}
                          />
                          <Bar dataKey="total">
                            {customerChartTop10.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div>
                    <DataTable
                      size="sm"
                      dataSource={customerDataShown}
                      columns={customerColumns}
                      pagination={{ pageSize: 10 }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
              <CardTitle>월별 매출</CardTitle>
              <Button
                variant="outline"
                onClick={() =>
                  exportXlsx(`월별_매출_${fileSuffix}`, monthlyAgg, [
                    { key: "month", label: "월" },
                    { key: "total", label: "매출합계" },
                  ])
                }
              >
                엑셀 다운로드
              </Button>
            </CardHeader>
            <CardContent>
              <DataTable
                size="sm"
                dataSource={monthlyAgg.map((d, i) => ({ key: i, ...d }))}
                columns={monthlyColumns}
                pagination={{ pageSize: 12 }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Statistics;
